import React, { useCallback, useState } from 'react';
import { Alert, Button, Card, Space, Table, Tag, Typography } from 'antd';
import { epcEstimationService } from '@/services/epcEstimation.service';

const { Text } = Typography;

/** Domain Wave 9 cert baseline (2.5k cost lines, adjusted scale). */
const CERT_BASELINE_MS: Record<string, number> = {
  'List BOQ': 158,
  'List cost lines': 1610,
  'Total cost summary': 3990,
  'Unified readiness': 11130,
};

export interface EpcEstimatePerformancePanelProps {
  estimateId: string;
  revisionId: string;
  costLineCount?: number;
  boqLineCount?: number;
}

interface MeasureRow {
  key: string;
  operation: string;
  ms: number | null;
  baselineMs: number | null;
  status: 'ok' | 'slow' | 'pending' | 'error';
  note?: string;
}

async function measure<T>(fn: () => Promise<T>): Promise<{ ms: number; ok: boolean }> {
  const t0 = performance.now();
  try {
    await fn();
    return { ms: Math.round(performance.now() - t0), ok: true };
  } catch {
    return { ms: Math.round(performance.now() - t0), ok: false };
  }
}

/**
 * EST3 UX2 Wave 9 — client-side latency measure (measure-first; no invented SLA).
 */
export const EpcEstimatePerformancePanel: React.FC<EpcEstimatePerformancePanelProps> = ({
  estimateId,
  revisionId,
  costLineCount = 0,
  boqLineCount = 0,
}) => {
  const [rows, setRows] = useState<MeasureRow[]>([]);
  const [running, setRunning] = useState(false);

  const runMeasure = useCallback(async () => {
    setRunning(true);
    const ops: Array<{ key: string; operation: string; run: () => Promise<unknown> }> = [
      {
        key: 'boq',
        operation: 'List BOQ',
        run: () => epcEstimationService.listClientBoq(estimateId, revisionId),
      },
      {
        key: 'cost',
        operation: 'List cost lines',
        run: () => epcEstimationService.listCostLines(estimateId, revisionId),
      },
      {
        key: 'summary',
        operation: 'Total cost summary',
        run: () => epcEstimationService.totalCostSummary(estimateId, revisionId),
      },
      {
        key: 'readiness',
        operation: 'Unified readiness',
        run: () => epcEstimationService.readiness(estimateId),
      },
    ];

    const next: MeasureRow[] = [];
    for (const op of ops) {
      next.push({
        key: op.key,
        operation: op.operation,
        ms: null,
        baselineMs: CERT_BASELINE_MS[op.operation] ?? null,
        status: 'pending',
      });
      setRows([...next]);
      const { ms, ok } = await measure(op.run);
      const baseline = CERT_BASELINE_MS[op.operation];
      const slow = baseline != null && ms > baseline * 1.5;
      next[next.length - 1] = {
        key: op.key,
        operation: op.operation,
        ms,
        baselineMs: baseline ?? null,
        status: ok ? (slow ? 'slow' : 'ok') : 'error',
        note: ok ? undefined : 'Request failed',
      };
      setRows([...next]);
    }
    setRunning(false);
  }, [estimateId, revisionId]);

  const virtualActive = costLineCount > 50;

  return (
    <Card size="small" title="Performance measure">
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Alert
          type="info"
          showIcon
          message="Measure-first — compare to domain Wave 9 cert baseline; no invented SLA."
        />
        <Space wrap>
          <Text type="secondary">
            BOQ lines: {boqLineCount} · Cost lines: {costLineCount}
          </Text>
          <Tag color={virtualActive ? 'blue' : 'default'}>
            Virtual list {virtualActive ? 'active' : 'off'} (&gt;50 rows)
          </Tag>
        </Space>
        <Button type="primary" loading={running} onClick={() => void runMeasure()}>
          Run measure
        </Button>
        {rows.length ? (
          <Table
            size="small"
            rowKey="key"
            pagination={false}
            dataSource={rows}
            columns={[
              { title: 'Operation', dataIndex: 'operation' },
              {
                title: 'Measured (ms)',
                dataIndex: 'ms',
                width: 120,
                render: (v: number | null) => (v == null ? '…' : v.toLocaleString()),
              },
              {
                title: 'Cert baseline (ms)',
                dataIndex: 'baselineMs',
                width: 140,
                render: (v: number | null) => (v == null ? '—' : v.toLocaleString()),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 100,
                render: (s: MeasureRow['status']) => (
                  <Tag
                    color={
                      s === 'ok' ? 'green' : s === 'slow' ? 'orange' : s === 'error' ? 'red' : 'default'
                    }
                  >
                    {s}
                  </Tag>
                ),
              },
              { title: 'Note', dataIndex: 'note', ellipsis: true },
            ]}
          />
        ) : null}
      </Space>
    </Card>
  );
};
